/**
 * useMaster - マスタデータ取得フック
 *
 * マスタAPIからデータを取得し、キャッシュする。
 * ドクター側の useMasterHooks と同等の機能を提供する。
 */

import { useState, useEffect, useCallback } from 'react';
import {
  masterRepository,
  type MasterCategory,
  type MasterItem,
  MASTER_KEY,
} from '../services/masterRepository';
import { useToken } from './useToken';

interface UseMasterReturn {
  /** マスタデータ全体 */
  categories: MasterCategory[];
  /** ローディング中か */
  isLoading: boolean;
  /** エラーメッセージ（取得失敗時） */
  error: string | null;
  /** カテゴリIDからアイテム一覧を取得 */
  getItems: (categoryId: string) => MasterItem[];
  /** カテゴリIDとアイテムIDからitemCodeを取得 */
  getItemCode: (categoryId: string, itemId: string) => string;
  /** カテゴリIDからフォーム用 { value, label } 一覧に変換 */
  getFormItems: (categoryId: string) => { value: string; label: string }[];
}

// モジュールレベルのキャッシュ（同一セッション内で再取得しない）
let cachedCategories: MasterCategory[] | null = null;
let fetchPromise: Promise<void> | null = null;

/** テスト用: キャッシュをリセットする */
export const resetMasterCache = (): void => {
  cachedCategories = null;
  fetchPromise = null;
};

export { MASTER_KEY };

export const useMaster = (): UseMasterReturn => {
  const [categories, setCategories] = useState<MasterCategory[]>(cachedCategories ?? []);
  const [isLoading, setIsLoading] = useState(cachedCategories === null);
  const [error, setError] = useState<string | null>(null);
  const token = useToken();

  useEffect(() => {
    if (cachedCategories !== null) {
      setCategories(cachedCategories);
      setIsLoading(false);
      return;
    }

    // 重複fetchを防止
    if (!fetchPromise) {
      fetchPromise = (async () => {
        try {
          const result = await masterRepository.getAll(token);
          if (result.success) {
            cachedCategories = result.data;
          } else {
            console.error('[useMaster] マスタデータ取得失敗:', result.error.message);
          }
        } catch (e) {
          console.error('[useMaster] マスタデータ取得エラー:', e);
        }
        fetchPromise = null;
      })();
    }

    fetchPromise.then(() => {
      if (cachedCategories) {
        setCategories(cachedCategories);
      } else {
        setError('マスタデータの取得に失敗しました');
      }
      setIsLoading(false);
    });
  }, [token]);

  const getItems = useCallback(
    (categoryId: string): MasterItem[] => {
      return categories.find(c => c.id === categoryId)?.item ?? [];
    },
    [categories],
  );

  const getItemCode = useCallback(
    (categoryId: string, itemId: string): string => {
      const item = categories
        .find(c => c.id === categoryId)
        ?.item.find(i => i.itemId === itemId);
      return item?.itemCode ?? '';
    },
    [categories],
  );

  const getFormItems = useCallback(
    (categoryId: string): { value: string; label: string }[] => {
      return (
        categories
          .find(c => c.id === categoryId)
          ?.item.map(i => ({ value: i.itemId, label: i.itemName })) ?? []
      );
    },
    [categories],
  );

  return { categories, isLoading, error, getItems, getItemCode, getFormItems };
};
